/**
 * 星球地图公开居民图层的查询（./publicMap.service）。
 * 读口从哪来：先看外面有没有用 PublicMapReaderProvider 换一个（测试、演示台用）；没有就按数据模式选——
 * live 用全站唯一的 HTTP 客户端，演示用空图层（不编位置）。照 communicator/usePetFriends 的写法；
 * shared 的 PetsService 开放后改成从 useServices().pets 取，这个 Provider 可以删掉。
 */
import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/api/client";
import { env } from "@/shared/config/env";
import { demoPublicMapReader, livePublicMapReader, loadPublicMap, type PublicMapLayer, type PublicMapReader } from "./publicMap.service";

const ReaderContext = createContext<PublicMapReader | null>(null);

export function PublicMapReaderProvider({ reader, children }: { reader: PublicMapReader; children: ReactNode }) {
  return <ReaderContext.Provider value={reader}>{children}</ReaderContext.Provider>;
}

let live: PublicMapReader | null = null;
let demo: PublicMapReader | null = null;

export function usePublicMapReader(): PublicMapReader {
  const injected = useContext(ReaderContext);
  if (injected) return injected;
  if (env.dataMode === "fixture") return (demo ??= demoPublicMapReader());
  return (live ??= livePublicMapReader(apiClient));
}

/** 查询键：模块自己的（shared/query 的 queryKeys 要报批才加）；访客与登录玩家看到同一份，不按人分。 */
export const PUBLIC_MAP_KEY = ["pets", "public-world-map"] as const;

export function usePublicMap() {
  const reader = usePublicMapReader();
  return useQuery<PublicMapLayer>({
    queryKey: PUBLIC_MAP_KEY,
    queryFn: ({ signal }) => loadPublicMap(reader, signal),
    staleTime: 30_000,
    retry: false,
  });
}
